import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { BookOpen, Calendar, User, ChevronLeft } from 'lucide-react';

interface Article {
  _id: string;
  title: string;
  slug: string;
  excerpt: string;
  category: string;
  tags: string[];
  coverImage?: string;
  author: string;
  publishedAt?: string;
  createdAt: string;
}

const categoryLabels: Record<string, string> = {
  all: 'כל הנושאים',
  fundraising: 'גיוס תרומות',
  'donor-community': 'קהילת תורמים',
  management: 'ניהול עמותה',
  legal: 'משפטי',
  finance: 'כספים',
  other: 'כללי',
};

const categoryColors: Record<string, string> = {
  fundraising: 'bg-rose-100 text-rose-700',
  'donor-community': 'bg-violet-100 text-violet-700',
  management: 'bg-blue-100 text-blue-700',
  legal: 'bg-amber-100 text-amber-700',
  finance: 'bg-green-100 text-green-700',
  other: 'bg-gray-100 text-gray-700',
};

const KnowledgePage: React.FC = () => {
  const [articles, setArticles] = useState<Article[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [selectedCategory, setSelectedCategory] = useState('all');
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);

  const fetchArticles = async (cat: string, p: number) => {
    setLoading(true);
    setError(null);
    try {
      const params = new URLSearchParams({ page: String(p), limit: '9' });
      if (cat !== 'all') params.set('category', cat);
      const res = await fetch(`https://amutech.co.il/api/articles?${params}`);
      if (!res.ok) throw new Error('שגיאה בטעינת מאמרים');
      const data = await res.json();
      setArticles(data.articles || []);
      setTotalPages(data.totalPages || 1);
    } catch (e: any) {
      setError(e.message || 'שגיאה בטעינת מאמרים');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchArticles(selectedCategory, page);
  }, [selectedCategory, page]);

  const handleCategoryChange = (cat: string) => {
    setSelectedCategory(cat);
    setPage(1);
  };

  const formatDate = (dateStr?: string) => {
    if (!dateStr) return '';
    return new Date(dateStr).toLocaleDateString('he-IL', {
      year: 'numeric',
      month: 'long',
      day: 'numeric',
    });
  };

  return (
    <div dir="rtl" className="min-h-screen bg-[#FAFAFA]">
      {/* Hero */}
      <div className="bg-gradient-to-l from-[#1565C0] to-[#6200EA] text-white py-10 md:py-16 px-4 md:px-6">
        <div className="max-w-5xl mx-auto text-center">
          <div className="flex justify-center mb-4">
            <BookOpen size={48} className="opacity-90" />
          </div>
          <h1 className="text-2xl md:text-4xl font-extrabold mb-3">מרכז הידע של Amutech</h1>
          <p className="text-base md:text-lg opacity-85 max-w-2xl mx-auto">
            מדריכים, מאמרים וטיפים מקצועיים לניהול עמותה מוצלחת — גיוס תרומות, ניהול קהילה, משפטי, ועוד
          </p>
        </div>
      </div>

      {/* Category Tabs */}
      <div className="bg-white border-b border-gray-200 sticky top-0 z-10 shadow-sm">
        <div className="max-w-5xl mx-auto px-4">
          <div className="flex gap-1 overflow-x-auto py-3 scrollbar-hide">
            {Object.entries(categoryLabels).map(([key, label]) => (
              <button
                key={key}
                onClick={() => handleCategoryChange(key)}
                className={`flex-shrink-0 px-4 py-2 rounded-full text-sm font-medium transition-all duration-200 ${
                  selectedCategory === key
                    ? 'bg-[#1565C0] text-white shadow-sm'
                    : 'text-gray-600 hover:bg-gray-100'
                }`}
              >
                {label}
              </button>
            ))}
          </div>
        </div>
      </div>

      {/* Content */}
      <div className="max-w-5xl mx-auto px-4 py-10">
        {loading && (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {[...Array(6)].map((_, i) => (
              <div key={i} className="bg-white rounded-2xl shadow-sm overflow-hidden animate-pulse">
                <div className="h-44 bg-gray-200" />
                <div className="p-5 space-y-3">
                  <div className="h-4 bg-gray-200 rounded w-1/3" />
                  <div className="h-5 bg-gray-200 rounded w-3/4" />
                  <div className="h-4 bg-gray-200 rounded" />
                  <div className="h-4 bg-gray-200 rounded w-2/3" />
                </div>
              </div>
            ))}
          </div>
        )}

        {error && (
          <div className="text-center py-16 text-red-500">
            <p className="text-lg">{error}</p>
            <button
              onClick={() => fetchArticles(selectedCategory, page)}
              className="mt-4 px-6 py-2 bg-[#1565C0] text-white rounded-xl hover:bg-blue-700 transition"
            >
              נסה שוב
            </button>
          </div>
        )}

        {!loading && !error && articles.length === 0 && (
          <div className="text-center py-20 text-gray-400">
            <BookOpen size={48} className="mx-auto mb-4 opacity-40" />
            <p className="text-xl font-medium">אין מאמרים עדיין בקטגוריה זו</p>
            <p className="text-sm mt-1">בקרוב יתווספו מאמרים חדשים</p>
          </div>
        )}

        {!loading && !error && articles.length > 0 && (
          <>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
              {articles.map((article) => (
                <Link
                  key={article._id}
                  to={`/knowledge/${article.slug}`}
                  className="group bg-white rounded-2xl shadow-sm hover:shadow-md border border-gray-100 overflow-hidden transition-all duration-200 flex flex-col"
                >
                  {/* Cover image */}
                  <div className="h-44 bg-gradient-to-br from-blue-50 to-indigo-100 overflow-hidden flex-shrink-0 relative">
                    {article.coverImage ? (
                      <img
                        src={article.coverImage}
                        alt={article.title}
                        className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300"
                      />
                    ) : (
                      <div className="w-full h-full flex items-center justify-center">
                        <BookOpen size={40} className="text-blue-300" />
                      </div>
                    )}
                    {/* Category badge */}
                    <span className={`absolute top-3 right-3 text-xs font-semibold px-2.5 py-1 rounded-full ${categoryColors[article.category] || categoryColors.other}`}>
                      {categoryLabels[article.category] || 'כללי'}
                    </span>
                  </div>

                  {/* Card body */}
                  <div className="p-5 flex flex-col flex-1">
                    <h2 className="text-base font-bold text-gray-900 mb-2 line-clamp-2 group-hover:text-[#1565C0] transition-colors">
                      {article.title}
                    </h2>
                    {article.excerpt && (
                      <p className="text-sm text-gray-500 mb-4 line-clamp-3 flex-1">
                        {article.excerpt}
                      </p>
                    )}
                    <div className="flex items-center gap-3 text-xs text-gray-400 mt-auto pt-3 border-t border-gray-100">
                      <span className="flex items-center gap-1">
                        <User size={12} />
                        {article.author}
                      </span>
                      <span className="flex items-center gap-1 mr-auto">
                        <Calendar size={12} />
                        {formatDate(article.publishedAt || article.createdAt)}
                      </span>
                    </div>
                  </div>
                </Link>
              ))}
            </div>

            {/* Pagination */}
            {totalPages > 1 && (
              <div className="flex justify-center items-center gap-3 mt-10">
                <button
                  onClick={() => setPage((p) => Math.max(1, p - 1))}
                  disabled={page === 1}
                  className="px-4 py-2 rounded-xl border border-gray-200 text-sm font-medium hover:bg-gray-50 disabled:opacity-40 disabled:cursor-not-allowed transition"
                >
                  <ChevronLeft size={16} className="inline rotate-180" /> הקודם
                </button>
                <span className="text-sm text-gray-500">
                  עמוד {page} מתוך {totalPages}
                </span>
                <button
                  onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
                  disabled={page === totalPages}
                  className="px-4 py-2 rounded-xl border border-gray-200 text-sm font-medium hover:bg-gray-50 disabled:opacity-40 disabled:cursor-not-allowed transition"
                >
                  הבא <ChevronLeft size={16} className="inline" />
                </button>
              </div>
            )}
          </>
        )}
      </div>
    </div>
  );
};

export default KnowledgePage;
